import { Grid } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { useQuill } from "react-quilljs";
import theme from "Theme/AppTheme";

type Props = {
    showToolbar: boolean;
    text: string;
    setText: (input: string) => void;
    showClear?: boolean;
};

const TextCardEditor = observer((props: Props) => {
    const { setText, text, showToolbar } = props;
    const modules = {
        toolbar: "#quill-toolbar",
    };
    const { quill, quillRef } = useQuill({ modules, bounds: "#full-editor" });

    const [focused, setFocused] = useState(false);

    // need this to properly implement clear functionality for this react-quilljs implementation
    const suppressChange = useRef(false);

    useEffect(() => {
        if (quill) {
            const currentHTML = quill.root.innerHTML;

            if (text === "" && currentHTML !== "<p><br></p>") {
                suppressChange.current = true;
                quill.setText("");
            } else if (text !== "" && text !== currentHTML) {
                suppressChange.current = true;
                quill.clipboard.dangerouslyPasteHTML(text);
            }
        }
    }, [quill, text]);

    useEffect(() => {
        if (quill) {
            const handler = () => {
                if (suppressChange.current) {
                    suppressChange.current = false;
                    return;
                }
                setText(quill.root.innerHTML);
            };
            quill.on("text-change", handler);
            quill.on("selection-change", handler);
            return () => {
                quill.off("text-change", handler);
                quill.off("selection-change", handler);
            };
        }
    }, [quill, setText]);

    return (
        <div style={{ height: "200px" }} id="full-editor">
            <div
                id="quill-toolbar"
                style={{
                    display: showToolbar ? "block" : "none", // need to do this because quill doesnt like to conditionally render the toolbar
                    borderColor: focused
                        ? theme.palette.primary.main
                        : undefined,
                }}
            >
                <button className="ql-bold"></button>
                <button className="ql-italic"></button>
                <button className="ql-underline"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-link"></button>
                <select className="ql-header">
                    <option value="1"></option>
                    <option value="2"></option>
                    <option selected></option>
                </select>
                <select className="ql-align">
                    <option selected></option>
                    <option value="center"></option>
                    <option value="right"></option>
                </select>
            </div>
            <div
                ref={quillRef}
                style={
                    focused ? { borderColor: theme.palette.primary.main } : {}
                }
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
            />
            {props.showClear && (
                <Grid container item xs={12} justifyContent={"flex-end"}>
                    <AcxButton
                        color="white"
                        onClick={() => {
                            props.setText("");
                        }}
                        disabled={!props.text}
                        containerStyle={{ paddingTop: "8px" }}
                    >
                        Clear
                    </AcxButton>
                </Grid>
            )}
        </div>
    );
});

export default TextCardEditor;
